<template>
  <form
      v-if="booking.period"
      class="payment"
  >
    <booking-summary
        :event="event"
        :booking="booking"
        class="mb-3"
    ></booking-summary>

    <hr />

    <div v-if="loading || saving" class="alert alert-warning d-flex align-items-center justify-content-center mb-3">
      <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
      <strong>{{ $t('loading') }}</strong>
    </div>

    <div
        v-show="!saving"
        id="payment-methods"
    ></div>

    <div class="d-flex justify-content-between align-items-center gap-4 mt-4">
      <button
          type="button"
          class="btn btn-link btn-sm ms-n2"
          :class="{ 'disabled': saving }"
          :disabled="saving"
          @click="$router.go(-1)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
             class="bi bi-arrow-left-short" viewBox="0 0 16 16">
          <path fill-rule="evenodd"
                d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5"/>
        </svg>
        <span class="ms-1">{{ $t('back') }}</span>
      </button>
    </div>
  </form>
</template>

<script>
import BookingSummary from '../components/BookingSummary';
import MultiSafepayService from '../services/MultiSafepayService';
import eventBus from '../eventBus';

export default {
  components: {
    BookingSummary
  },

  props: [
    'event',
    'booking',
    'saving'
  ],

  data() {
    return {
      stylesheetLoaded: false,
      scriptLoaded: false,
      loadingApiToken: false,
      apiToken: null,
    }
  },

  computed: {
    loading() {
      return !this.stylesheetLoaded || !this.scriptLoaded || this.loadingApiToken;
    },

    totalLockers() {
      return this.booking.totalLockers;
    },
  },

  watch: {
    '$route': 'init',
  },

  mounted() {
    this.loadStylesheet();
    this.loadScript();
  },

  created() {
    this.init();
  },

  methods: {
    /**
     * Initialise payment and get API token.
     */
    init() {
      if (!this.booking.period || this.totalLockers === 0) {
        this.$router.push('/event/' + this.event.code);
        return;
      }

      this.loadingApiToken = true;

      MultiSafepayService.getApiToken(this.event.payment.profile)
          .then(response => {
            this.loadingApiToken = false;
            this.apiToken = response.data.api_token;

            this.$nextTick(() => {
              this.initPayment();
            });
          });
    },

    /**
     * Load the MultiSafepay stylesheet.
     */
    loadStylesheet() {
      if (this.stylesheetLoaded) {
        return;
      }

      if (document.getElementById('msp-stylesheet')) {
        this.stylesheetLoaded = true;
        return;
      }

      const link = document.createElement('link');
      link.id = 'msp-stylesheet';
      link.rel = 'stylesheet';
      link.href = this.event.payment.environment === 'test'
          ? 'https://testpay.multisafepay.com/sdk/components/v2/components.css'
          : 'https://pay.multisafepay.com/sdk/components/v2/components.css';

      link.onload = () => {
        this.stylesheetLoaded = true;
      }

      document.head.insertBefore(link, document.head.firstChild);
    },

    /**
     * Load the MultiSafepay script.
     */
    loadScript() {
      if (this.scriptLoaded) {
        return;
      }

      if (document.getElementById('msp-script')) {
        this.scriptLoaded = true;
        return;
      }

      const script = document.createElement('script');
      script.id = 'msp-script';
      script.src = this.event.payment.environment === 'test'
          ? 'https://testpay.multisafepay.com/sdk/components/v2/components.js'
          : 'https://pay.multisafepay.com/sdk/components/v2/components.js';
      script.async = true;

      script.onload = () => {
        this.scriptLoaded = true;
      }

      document.body.appendChild(script);
    },

    /**
     * Initialise MultiSafepay payment.
     */
    initPayment() {
      if (this.loading) {
        // Try again as long as assets are loading
        setTimeout(() => this.initPayment(), 500);
        return;
      }

      // eslint-disable-next-line no-undef
      const multiSafepay = new MultiSafepay({
        env: this.event.payment.environment,
        apiToken: this.apiToken,
        order: {
          currency: 'EUR',
          amount: this.booking.calculateTotalAmount(this.event) * 100,
          customer: {
            locale: this.$i18n.locale,
            // Setting country to 'BE' is a workaround as suggested by MSP in ticket 252795.
            country: 'BE',
          },
          // Enable QR code for Bancontact
          payment_options: {
            settings: {
              connect: {
                qr: {
                  enabled: true,
                  autoload: false,
                },
              },
            },
          },
        },
      });

      const vue = this;

      multiSafepay.init('dropin', {
        container: '#payment-methods',
        onEvent: function (state) {
          if (state && state.type === 'payment' && state.success && state.data.paymentData) {
            vue.booking.payment_data = state.data.paymentData;
            vue.$emit('ready', vue.booking, true);
          }
        },
        onGetQR: function (state) {
          if (state && state.orderData && state.orderData.payment_data) {
            vue.booking.payment_data = state.orderData.payment_data;
            vue.$emit('ready', vue.booking, true);
          }
        },
        onError: function () {
          eventBus.$emit('error', vue.$t('error'), true);
        },
        onSubmit: function () {
          if (multiSafepay.hasErrors()) {
            return;
          }

          vue.booking.payment_data = multiSafepay.getPaymentData();
          vue.$emit('ready', vue.booking, true);
        }
      });
    }
  }
}
</script>
